import React, { useState } from "react"
import { Image, StyleSheet, Text, View } from "react-native"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import studentCopy from "@assets/inscription/live-student-copy.webp"
import samPointing from "@assets/inscription/live-sam-pointing.webp"
import studentListening from "@assets/inscription/live-student-listening.webp"
import studentWriting from "@assets/inscription/live-student-writing.webp"
import tutorExplaining from "@assets/inscription/live-tutor-explaining.webp"
import tutorReading from "@assets/inscription/live-tutor-reading.webp"
import tutorWatching from "@assets/inscription/live-tutor-watching.webp"
import { brand } from "../../design-system/theme/tokens/colors"

/** Le dessin se compose à cette taille, puis se met à l'échelle de sa place. */
const WIDTH = 160
const HEIGHT = 120

/**
 * Un temps de l'heure, en tuiles superposées — un schéma, pas un écran : à la
 * taille d'un téléphone, des boutons ne se lisent pas. L'élève est toujours en
 * bas à droite.
 *
 * - `dialogue` : le masteur parle, en grand ; devant lui, la copie de l'élève
 *   avec ses repères de correction ; l'élève l'écoute.
 * - `training` : l'énoncé en grand, que Sam montre du doigt ; l'élève écrit.
 *   Le masteur est derrière, plus clair, occupé à lire — ou, quand il reste
 *   avec l'élève (`beside`), devant, à côté de lui, et il l'observe.
 *
 * Décoratif : les mots de chaque temps sont écrits sous le dessin.
 */
const MomentTiles = ({ mode, beside = false }) => {
    const [width, setWidth] = useState(0)
    const training = mode === "training"

    return (
        <View
            aria-hidden
            style={[
                styles.slot,
                width > 0 && { height: (width * HEIGHT) / WIDTH },
            ]}
            onLayout={(event) => setWidth(event.nativeEvent.layout.width)}
        >
            {width > 0 ? (
                <View
                    style={[
                        styles.canvas,
                        {
                            left: (width - WIDTH) / 2,
                            top: ((width / WIDTH) * HEIGHT - HEIGHT) / 2,
                            transform: [{ scale: width / WIDTH }],
                        },
                    ]}
                >
                    {training ? <Training beside={beside} /> : <Dialogue />}
                    <Photo
                        source={training ? studentWriting : studentListening}
                        style={styles.student}
                    />
                </View>
            ) : null}
        </View>
    )
}

const Dialogue = () => (
    <>
        <Photo source={tutorExplaining} style={styles.lead} />
        <View style={styles.copy}>
            <Photo source={studentCopy} style={styles.sheet} />
            {/* La correction, posée sur l'encre : la ligne juste cochée,
                l'erreur entourée. */}
            <Mark icon="check" style={styles.rightMark} />
            <View style={styles.wrong} />
            <Mark icon="times" style={styles.wrongMark} />
        </View>
    </>
)

const Training = ({ beside }) => (
    <>
        {beside ? null : (
            <Photo source={tutorReading} style={styles.behind} plain />
        )}
        <View style={styles.statement}>
            <View style={styles.sum}>
                <Fraction top="3" bottom="4" />
                <Text style={styles.sign}>+</Text>
                <Fraction top="2" bottom="5" />
                <Text style={styles.sign}>=</Text>
                <Text style={styles.unknown}>?</Text>
            </View>
            <View style={styles.rule} />
            <Image
                source={samPointing}
                resizeMode="contain"
                style={styles.sam}
            />
        </View>
        {beside ? <Photo source={tutorWatching} style={styles.beside} /> : null}
    </>
)

/** Une tuile de visio : la photo, coins ronds, détourée de ce qu'elle recouvre. */
const Photo = ({ source, style, plain = false }) => (
    <View style={[styles.photo, !plain && styles.edge, style]}>
        <Image source={source} resizeMode="cover" style={styles.whole} />
    </View>
)

/** Une pastille de correction, posée sur la copie. */
const Mark = ({ icon, style }) => (
    <View style={[styles.mark, style]}>
        <FontAwesome5 name={icon} size={6} color={brand.white} solid />
    </View>
)

/** Une fraction posée : numérateur, barre, dénominateur. */
const Fraction = ({ top, bottom }) => (
    <View style={styles.fraction}>
        <Text style={styles.digit}>{top}</Text>
        <View style={styles.fractionBar} />
        <Text style={styles.digit}>{bottom}</Text>
    </View>
)

const styles = StyleSheet.create({
    slot: { width: "100%", aspectRatio: WIDTH / HEIGHT },
    canvas: { position: "absolute", width: WIDTH, height: HEIGHT },
    // Une image du bundle arrive avec ses dimensions d'origine : sa taille se
    // donne toujours.
    whole: { width: "100%", height: "100%" },
    photo: {
        position: "absolute",
        borderRadius: 10,
        overflow: "hidden",
        backgroundColor: brand.lavender,
    },
    edge: { borderWidth: 2, borderColor: brand.white },

    // L'élève, toujours en bas à droite.
    student: { left: 102, top: 72, width: 58, height: 48 },

    // Dialogue : le masteur en grand, la copie posée devant lui.
    lead: { left: 26, top: 0, width: 120, height: 88, borderRadius: 12 },
    // La copie : une photo de cahier, posée de biais devant le masteur. La
    // feuille coupe sa photo, pas ses marques : la coche tient sur son bord.
    copy: {
        position: "absolute",
        left: 2,
        top: 42,
        width: 58,
        height: 75,
        transform: [{ rotate: "-5deg" }],
    },
    sheet: {
        width: "100%",
        height: "100%",
        borderRadius: 5,
        backgroundColor: brand.white,
    },
    // Les marques suivent l'écriture de la photo, mesurée sur la feuille : la
    // première ligne (juste) est en y 25-35 et sort par la droite ; l'erreur
    // « 4/2 » de la seconde est en x 45-51, y 40-50. Une autre photo, d'autres
    // mesures.
    wrong: {
        position: "absolute",
        left: 42,
        top: 37,
        width: 13,
        height: 15,
        borderRadius: 4,
        borderWidth: 1.5,
        borderColor: brand.error,
    },
    mark: {
        position: "absolute",
        width: 12,
        height: 12,
        borderRadius: 6,
        alignItems: "center",
        justifyContent: "center",
        borderWidth: 1,
        borderColor: brand.white,
    },
    rightMark: { left: 51, top: 24, backgroundColor: brand.progress },
    wrongMark: { left: 46, top: 49, backgroundColor: brand.error },

    // Entraînement : l'énoncé en grand.
    statement: {
        position: "absolute",
        left: 0,
        top: 18,
        width: 128,
        height: 70,
        gap: 6,
        paddingTop: 17,
        paddingLeft: 14,
        borderRadius: 12,
        borderWidth: 1.5,
        borderColor: brand.hairline,
        backgroundColor: brand.white,
    },
    // Sam, à droite de l'énoncé, le montre du doigt : son dessin pointe à
    // droite, il est retourné.
    sam: {
        position: "absolute",
        left: 68,
        top: 0,
        width: 56,
        height: 56,
        transform: [{ scaleX: -1 }],
    },
    sum: { flexDirection: "row", alignItems: "center", gap: 5 },
    fraction: { alignItems: "center" },
    digit: {
        fontFamily: "Montserrat-ExtraBold",
        fontSize: 10.5,
        lineHeight: 12,
        color: brand.ink,
    },
    fractionBar: {
        alignSelf: "stretch",
        height: 1.5,
        marginVertical: 1,
        borderRadius: 1,
        backgroundColor: brand.ink,
    },
    sign: {
        fontFamily: "Montserrat-Bold",
        fontSize: 11,
        lineHeight: 13,
        color: brand.ink,
    },
    unknown: {
        fontFamily: "Montserrat-ExtraBold",
        fontSize: 13,
        lineHeight: 15,
        color: brand.focused,
    },
    rule: {
        width: 52,
        height: 3,
        borderRadius: 2,
        backgroundColor: brand.hairline,
    },
    // Le masteur, derrière : plus petit, plus clair, il dépasse d'un coin.
    behind: { left: 112, top: 2, width: 48, height: 37, opacity: 0.55 },
    // Le masteur resté avec l'élève : devant, à côté de lui.
    beside: { left: 48, top: 72, width: 58, height: 48 },
})

export default MomentTiles
